import type { EngagementPhase } from "@/types/content";
import {
  PhoneCall,
  Compass,
  ClipboardList,
  PenTool,
  Code2,
  FlaskConical,
  Rocket,
  GraduationCap,
  LifeBuoy,
} from "@/icons";

/**
 * The complete client engagement journey — from first conversation to
 * long-term partnership. Each phase carries its activities, deliverables and
 * a typical duration so expectations are clear from the start.
 */
export const engagementPhases: EngagementPhase[] = [
  {
    index: "01",
    title: "Consultation",
    icon: PhoneCall,
    summary:
      "A free, no-obligation conversation to understand your goals, constraints and what success looks like.",
    activities: [
      "Understand your business and objectives",
      "Clarify scope, timeline and budget expectations",
      "Identify risks and quick wins",
    ],
    deliverables: ["Fit assessment", "High-level direction", "Clear next steps"],
    duration: "1–2 days",
  },
  {
    index: "02",
    title: "Discovery",
    icon: Compass,
    summary:
      "Focused research and workshops to align on users, workflows and the problem worth solving.",
    activities: [
      "Stakeholder and user interviews",
      "Workflow and data mapping",
      "Competitive and technical assessment",
    ],
    deliverables: ["Discovery report", "User & journey maps", "Success metrics"],
    duration: "1–2 weeks",
  },
  {
    index: "03",
    title: "Planning",
    icon: ClipboardList,
    summary:
      "A pragmatic roadmap, architecture and scope — so we build the right thing in the right order.",
    activities: [
      "Solution architecture and tech decisions",
      "Roadmap and milestone planning",
      "Scope definition and estimates",
    ],
    deliverables: ["Product roadmap", "Architecture plan", "Fixed estimate"],
    duration: "3–5 days",
  },
  {
    index: "04",
    title: "Design",
    icon: PenTool,
    summary:
      "Prototype, validate and refine the experience with real users before a line of production code.",
    activities: [
      "Wireframes and interactive prototypes",
      "Design system and UI direction",
      "Usability validation and iteration",
    ],
    deliverables: ["Design system", "High-fidelity UI", "Validated prototype"],
    duration: "2–4 weeks",
  },
  {
    index: "05",
    title: "Development",
    icon: Code2,
    summary:
      "Engineering in tight, transparent loops — tested, reviewed and demoable every step of the way.",
    activities: [
      "Frontend, backend and integrations",
      "Continuous code review and testing",
      "Regular demos and progress visibility",
    ],
    deliverables: ["Working software", "Test coverage", "Sprint demos"],
    duration: "6–16 weeks",
  },
  {
    index: "06",
    title: "Testing & QA",
    icon: FlaskConical,
    summary:
      "Rigorous quality, accessibility and performance testing so launch day is a non-event.",
    activities: [
      "Functional and regression testing",
      "Accessibility (WCAG 2.2 AA) and performance audits",
      "Security review and hardening",
    ],
    deliverables: ["QA sign-off", "Accessibility report", "Performance budget met"],
    duration: "1–2 weeks",
  },
  {
    index: "07",
    title: "Deployment",
    icon: Rocket,
    summary:
      "A confident, monitored release with rollback plans and observability in place from minute one.",
    activities: [
      "Production infrastructure and CI/CD",
      "Monitoring, logging and alerting",
      "Staged rollout with rollback ready",
    ],
    deliverables: ["Live product", "Monitoring dashboards", "Runbook"],
    duration: "2–4 days",
  },
  {
    index: "08",
    title: "Training",
    icon: GraduationCap,
    summary:
      "We hand over confidently — your team learns the product, the code and how to run it.",
    activities: [
      "Team walkthroughs and enablement",
      "Documentation and handover",
      "Admin and content training",
    ],
    deliverables: ["Documentation", "Training sessions", "Handover pack"],
    duration: "3–5 days",
  },
  {
    index: "09",
    title: "Support & Growth",
    icon: LifeBuoy,
    summary:
      "Launch is the start. We monitor, optimise and evolve the product as a long-term partner.",
    activities: [
      "Proactive monitoring and SLAs",
      "Ongoing optimisation and iteration",
      "Roadmap partnership and new features",
    ],
    deliverables: ["Support SLA", "Optimisation cadence", "Evolving roadmap"],
    duration: "Ongoing",
  },
];
